// Checks for a person's details on the sign-up and profile forms. Each check
// returns the message to show under the field, or "" when the value is fine.
// The server checks the same rules (backend/src/schemas/auth.schema.js and
// profile.schemas.js), so the two never disagree.

// starts with a letter (accents too: José, Zoë), then letters, spaces, ' . -
// e.g. D'Souza, Anne-Marie, Ravi K.
const NAME_PATTERN = /^\p{L}[\p{L} '.-]*$/u;

const EMAIL_PATTERN =
  /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

// spaces, dashes, dots, brackets and one leading + are fine while typing
const PHONE_SEPARATORS = /[\s\-().]/g;

export const PHONE_MIN_DIGITS = 10;
export const PHONE_MAX_DIGITS = 15;
export const PASSWORD_MIN_LENGTH = 8;

export const NAME_MESSAGE = "can contain letters, spaces, ' . and - only.";

const text = (value) =>
  value === null || value === undefined ? "" : String(value).trim();

// "+91 98765-43210" -> "919876543210"; "" when it isn't a phone number
export const phoneDigits = (value) => {
  const digits = text(value).replace(PHONE_SEPARATORS, "").replace(/^\+/, "");
  return /^[0-9]+$/.test(digits) ? digits : "";
};

export const nameError = (label, value) => {
  const name = text(value);
  if (!name) return `${label} is required.`;
  if (!NAME_PATTERN.test(name)) return `${label} ${NAME_MESSAGE}`;
  return "";
};

export const emailError = (value) => {
  const email = text(value);
  if (!email) return "Email is required.";
  return EMAIL_PATTERN.test(email) ? "" : "Enter a valid email address.";
};

// an empty optional number is fine
export const phoneError = (label, value, { required = true } = {}) => {
  if (!text(value)) return required ? `${label} is required.` : "";
  const digits = phoneDigits(value);
  if (!digits) return `${label} can contain numbers only.`;
  if (digits.length < PHONE_MIN_DIGITS || digits.length > PHONE_MAX_DIGITS)
    return `${label} needs ${PHONE_MIN_DIGITS} to ${PHONE_MAX_DIGITS} digits.`;
  return "";
};

// an empty optional password means "keep the current one" (profile)
export const passwordError = (value, { required = true } = {}) => {
  const password = value || "";
  if (!password) return required ? "Password is required." : "";
  return password.length < PASSWORD_MIN_LENGTH
    ? `Password needs at least ${PASSWORD_MIN_LENGTH} characters.`
    : "";
};

// the checks of one form, field name -> check(value, allValues)
const PERSON_CHECKS = {
  firstName: (v) => nameError("First name", v),
  lastName: (v) => nameError("Last name", v),
  email: (v) => emailError(v),
  contactNumber: (v) => phoneError("Contact number", v),
  alternateContactNumber: (v) =>
    phoneError("Alternate contact number", v, { required: false }),
};

const REGISTER_CHECKS = {
  ...PERSON_CHECKS,
  username: (v) => {
    if (!text(v)) return "Username is required.";
    return text(v).length < 3 ? "Username needs at least 3 characters." : "";
  },
  password: (v) => passwordError(v),
  confirmpassword: (v, all) =>
    v === all.password ? "" : "Passwords do not match.",
};

const PROFILE_CHECKS = {
  ...PERSON_CHECKS,
  password: (v) => passwordError(v, { required: false }),
};

// every problem of the form at once, e.g. { lastName: "Last name ..." }
const formErrors = (checks, values) =>
  Object.keys(checks).reduce((errors, name) => {
    const message = checks[name](values[name], values);
    return message ? { ...errors, [name]: message } : errors;
  }, {});

export const registerErrors = (values) => formErrors(REGISTER_CHECKS, values);
export const profileErrors = (values) => formErrors(PROFILE_CHECKS, values);

// phone box -> what the server stores: a number, or null when left empty
export const phoneValue = (value) => {
  const digits = phoneDigits(value);
  return digits ? Number(digits) : null;
};

export const FIX_FIELDS_MESSAGE = "Please fix the fields marked in red.";
